import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import TrackerPage from '../../features/tracker/components/TrackerPage';
import MyOrganisationPanel from '../../features/organisations/components/MyOrganisationPanel';
import ProfileCertificatesSection from '../../features/certificates/components/ProfileCertificatesSection';
import { useAuth } from '../../features/auth/hooks/useAuth';
import { authAPI } from '../../features/auth/api/authAPI';
import { getLnatSafeApiBase, getBaseApiUrl } from '../../config/api';
import { isPathbuddyHost } from '../../config/eduHost';
import { useProductBackHandler } from '../ProductBackButton/ProductBackProvider';
import RedoOnboardingButton from './RedoOnboardingButton';
import { EDU_CANONICAL_BASE } from '../../config/appBase';

import './ProfilePage.css';

const OXFORD_COLLEGES = [
  'All Souls College',
  'Balliol College',
  'Brasenose College',
  'Christ Church',
  'Corpus Christi College',
  'Exeter College',
  'Green Templeton College',
  'Harris Manchester College',
  'Hertford College',
  'Jesus College',
  'Keble College',
  'Kellogg College',
  'Lady Margaret Hall',
  'Linacre College',
  'Lincoln College',
  'Magdalen College',
  'Mansfield College',
  'Merton College',
  'New College',
  'Nuffield College',
  'Oriel College',
  'Pembroke College',
  'The Queen\'s College',
  'Reuben College',
  'Somerville College',
  'St Anne\'s College',
  'St Antony\'s College',
  'St Catherine\'s College',
  'St Cross College',
  'St Edmund Hall',
  'St Hilda\'s College',
  'St Hugh\'s College',
  'St John\'s College',
  'St Peter\'s College',
  'Trinity College',
  'University College',
  'Wadham College',
  'Wolfson College',
  'Worcester College'
];

const CAMBRIDGE_COLLEGES = [
  'Christ\'s College',
  'Churchill College',
  'Clare College',
  'Clare Hall',
  'Corpus Christi College',
  'Darwin College',
  'Downing College',
  'Emmanuel College',
  'Fitzwilliam College',
  'Girton College',
  'Gonville & Caius College',
  'Homerton College',
  'Hughes Hall',
  'Jesus College',
  'King\'s College',
  'Lucy Cavendish College',
  'Magdalene College',
  'Murray Edwards College',
  'Newnham College',
  'Pembroke College',
  'Peterhouse',
  'Queens\' College',
  'Robinson College',
  'Selwyn College',
  'Sidney Sussex College',
  'St Catharine\'s College',
  'St Edmund\'s College',
  'St John\'s College',
  'Trinity College',
  'Trinity Hall',
  'Wolfson College'
];

// Custom Accessible Rounded Select Component
function CustomSelect({ value, onChange, options, placeholder = 'Select an option' }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find((opt) => opt.value === value);
  const displayLabel = selectedOption ? selectedOption.label : placeholder;

  return (
    <div className="custom-select-container" ref={dropdownRef}>
      <button
        type="button"
        className={`custom-select-trigger ${isOpen ? 'open' : ''} ${!value ? 'is-placeholder' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span>{displayLabel}</span>
        <svg
          className={`custom-select-arrow ${isOpen ? 'rotated' : ''}`}
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--iv-head)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {isOpen && (
        <ul className="custom-select-menu" role="listbox">
          {options.map((opt) => {
            const isSelected = opt.value === value;
            return (
              <li
                key={opt.value}
                className={`custom-select-option ${isSelected ? 'selected' : ''}`}
                role="option"
                aria-selected={isSelected}
                onClick={() => {
                  onChange(opt.value);
                  setIsOpen(false);
                }}
              >
                <span>{opt.label}</span>
                {isSelected && (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// Grade format validation function (matches database constraint)
const validateGradeFormat = (grade) => {
  if (!grade || grade.trim() === '') return true; // Empty is allowed
  
  // A-level format: A*, A, B+, etc.
  const aLevelPattern = /^[A-F][*+-]?$/;
  // GCSE format: 9, 8, 7, 9*, etc.
  const gcsePattern = /^[0-9][*+-]?$/;
  // UK degree format
  const ukDegreePattern = /^(First|Upper Second|Lower Second|Third|Pass|Fail|Distinction|Merit)$/;
  
  return aLevelPattern.test(grade) || gcsePattern.test(grade) || ukDegreePattern.test(grade);
};

const ProfilePage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, updateUser } = useAuth();
  const handleProductBack = useCallback(() => {
    navigate('/apps');
    window.scrollTo(0, 0);
  }, [navigate]);
  useProductBackHandler(handleProductBack);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState(() => searchParams.get('section') || 'personal');
  const [profileData, setProfileData] = useState({
    // Basic Profile
    first_name: '',
    last_name: '',
    creator_slug: '',
    dob: '',
    bio: '',
    phone: '',
    linkedin_url: '',
    personal_website: '',
    professional_summary: '',
    
    // Address
    address_line_1: '',
    address_line_2: '',
    city: '',
    state_province: '',
    postal_code: '',
    country: '',
    
    // Academic Info
    student_level: 'undecided',
    primary_subject: 'undecided',
    
    // Education entries
    education: [],

    // Subject entries,
    subjects: [],
    
    // Experience
    experiences: [],
    
    // Skills
    skills: [],
    
    // Languages
    languages: [],

    // Target applications (Target schools)
    target_applications: [],

    // OXBRIDGE FIELDS (NEW)
    is_oxbridge_candidate: false,
    oxbridge_university: '',
    oxbridge_college: '',
    is_degree_apprenticeship_candidate: false
  });

  // Helper: refresh token on 401 and retry the fetch once (shared serialized refresh)
  const fetchWithRefresh = async (url, options = {}) => {
    let token = authAPI.getAccessToken();
    const headers = { ...options.headers, 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
    let response = await fetch(url, { ...options, headers });
    if (response.status === 401 && token) {
      try {
        const newToken = await authAPI.refreshAccessToken();
        const retryHeaders = { ...options.headers, 'Authorization': `Bearer ${newToken}`, 'Content-Type': 'application/json' };
        response = await fetch(url, { ...options, headers: retryHeaders });
      } catch (refreshErr) {
        console.warn('Token refresh failed:', refreshErr);
      }
    }
    return response;
  };

  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState('');
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // Section configuration - removed emojis
  const hideBilling = isPathbuddyHost(
    typeof window !== 'undefined' ? window.location.hostname : ''
  );
  const sections = useMemo(() => {
    const all = [
      {
        id: 'personal',
        label: 'Personal',
        description: 'Basic information and contact details'
      },
      {
        id: 'education',
        label: 'Education',
        description: 'Academic background and qualifications'
      },
      {
        id: 'subjects',
        label: 'Subjects',
        description: 'Individual subjects with grades and achievements'
      },
      {
        id: 'experience',
        label: 'Experience',
        description: 'Work history and professional experience'
      },
      {
        id: 'skills',
        label: 'Skills',
        description: 'Technical skills and certifications'
      },
      {
        id: 'languages',
        label: 'Languages',
        description: 'Language proficiency levels'
      },
      {
        id: 'target_schools',
        label: 'Target schools',
        description: 'University choices, target grades and entrance tests'
      },
      {
        id: 'groups',
        label: 'School',
        description: 'Your school, leaving year and groups'
      },
      {
        id: 'tracker',
        label: 'Tracker',
        description: 'Upload CV, cover letter and personal statement, and see teacher feedback'
      },
      {
        id: 'certificates',
        label: 'Certificates & Credentials',
        description: 'Official verified IVORI certificates, digital credentials, and LinkedIn badges'
      },
      {
        id: 'billing',
        label: 'Billing & Plan',
        description: 'Subscription details, payment methods, invoices and plan management'
      },
      {
        id: 'security',
        label: 'Security',
        description: 'Password and account settings'
      }
    ];
    return hideBilling ? all.filter((s) => s.id !== 'billing') : all;
  }, [hideBilling]);

  useEffect(() => {
    const section = searchParams.get('section');
    if (!section) return;
    const allowed = hideBilling
      ? sections.filter((s) => s.id !== 'billing')
      : sections;
    if (allowed.some((s) => s.id === section)) {
      setActiveSection(section);
    }
    // Read once on mount / when billing visibility changes; tab clicks write the URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hideBilling, sections]);

  useEffect(() => {
    if (hideBilling && activeSection === 'billing') {
      setActiveSection('personal');
    }
  }, [hideBilling, activeSection]);

  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId);
    const next = new URLSearchParams(searchParams);
    if (sectionId === 'personal') {
      next.delete('section');
    } else {
      next.set('section', sectionId);
    }
    if (sectionId !== 'tracker') {
      next.delete('studentId');
    }
    setSearchParams(next, { replace: true });
  };
  // Load user profile data on mount
  useEffect(() => {
    const onTourAction = (event) => {
      const { profileSection } = event.detail || {};
      if (profileSection) setActiveSection(profileSection);
    };
    window.addEventListener('pathbuddy-tour-action', onTourAction);
    return () => window.removeEventListener('pathbuddy-tour-action', onTourAction);
  }, []);

  useEffect(() => {
    const loadUserProfile = async () => {
        // Helper: derive first/last from user.name if not available
        const deriveNames = (u) => {
          let fn = u.first_name || '';
          let ln = u.last_name || '';
          if (!fn && u.name) {
            const parts = u.name.trim().split(' ');
            fn = parts[0] || '';
            ln = parts.slice(1).join(' ') || '';
          }
          return { fn, ln };
        };

        try {
            setLoading(true);
            setErrors({}); // Clear any previous errors
            
            const token = localStorage.getItem('pathbuddy_access_token') || sessionStorage.getItem('pathbuddy_access_token');

            if (!token) {
            console.error('No authentication token found');
            // Pre-populate with existing user data from auth context if available
            if (user) {
                console.log('No token, using auth context data:', user);
                const { fn, ln } = deriveNames(user);
                setProfileData(prev => ({
                ...prev,
                first_name: fn,
                last_name: ln,
                bio: user.bio || '',
                student_level: user.student_level || 'undecided',
                primary_subject: user.primary_subject || 'undecided'
                }));
            }
            setLoading(false);
            return;
            }

            console.log('Loading profile with token:', token.substring(0, 20) + '...');
            
            // FORCE BACKEND URL - Same as save function
            const backendUrl = getLnatSafeApiBase('https://hammerhead-app-8cr38.ondigitalocean.app');
            const apiUrl = `${backendUrl}/api/auth/profile`;
            console.log('Making load request to:', apiUrl);

            try {
            const response = await fetchWithRefresh(apiUrl, {
                method: 'GET'
            });

            console.log('Profile response status:', response.status);
            console.log('Profile response headers:', Object.fromEntries(response.headers.entries()));

            if (response.ok) {
                const data = await response.json();
                console.log('Profile data loaded successfully:', data);
                // If API returned empty name, fall back to auth context name
                let apiFn = data.first_name || '';
                let apiLn = data.last_name || '';
                if (!apiFn && user && user.name) {
                  const { fn, ln } = deriveNames(user);
                  apiFn = fn;
                  apiLn = apiLn || ln;
                }

                setProfileData(prev => ({
                ...prev,
                ...data,
                // Ensure arrays exist and handle undefined values
                education: Array.isArray(data.education) ? data.education : [],
                experiences: Array.isArray(data.experiences) ? data.experiences : [],
                skills: Array.isArray(data.skills) ? data.skills : [],
                languages: Array.isArray(data.languages) ? data.languages : [],
                first_name: apiFn,
                last_name: apiLn,
                dob: data.dob || '',
                bio: data.bio || '',
                phone: data.phone || '',
                linkedin_url: data.linkedin_url || '',
                personal_website: data.personal_website || '',
                professional_summary: data.professional_summary || '',
                address_line_1: data.address_line_1 || '',
                address_line_2: data.address_line_2 || '',
                city: data.city || '',
                state_province: data.state_province || '',
                postal_code: data.postal_code || '',
                country: data.country || '',
                student_level: data.student_level || 'undecided',
                primary_subject: data.primary_subject || 'undecided',
                creator_slug: data.creator_slug || ''
                }));
                
                console.log('Profile form populated with:', {
                student_level: data.student_level || 'undecided',
                primary_subject: data.primary_subject || 'undecided'
                });
            } else {
                const errorText = await response.text();
                console.error('Profile load error:', response.status, errorText);
                
                let errorData = {};
                try {
                errorData = JSON.parse(errorText);
                } catch (e) {
                console.warn('Could not parse error response as JSON');
                }
                
                if (response.status === 401) {
                setErrors({ general: 'Session expired. Please log in again.' });
                navigate('/login');
                } else if (response.status === 404) {
                console.log('Profile not found, using auth context data');
                // Profile doesn't exist yet, use auth context data
                if (user) {
                    const { fn, ln } = deriveNames(user);
                    setProfileData(prev => ({
                    ...prev,
                    first_name: fn,
                    last_name: ln,
                    bio: user.bio || '',
                    student_level: user.student_level || 'undecided',
                    primary_subject: user.primary_subject || 'undecided'
                    }));
                }
                } else {
                setErrors({ general: errorData.error || `Failed to load profile (${response.status})` });
                }
            }
            } catch (networkError) {
            console.error('Network error details:', networkError);
            setErrors({ general: 'Network error. Please check your internet connection.' });
            
            // Fall back to auth context data if available
            if (user) {
                console.log('Network error, falling back to auth context:', user);
                const { fn, ln } = deriveNames(user);
                setProfileData(prev => ({
                ...prev,
                first_name: fn,
                last_name: ln,
                bio: user.bio || '',
                student_level: user.student_level || 'undecided',
                primary_subject: user.primary_subject || 'undecided'
                }));
            }
            }
        } catch (error) {
            console.error('Unexpected error loading profile:', error);
            setErrors({ general: 'An unexpected error occurred. Please try again.' });
            
            // Fall back to auth context data
            if (user) {
            const { fn, ln } = deriveNames(user);
            setProfileData(prev => ({
                ...prev,
                first_name: fn,
                last_name: ln,
                bio: user.bio || '',
                student_level: user.student_level || 'undecided',
                primary_subject: user.primary_subject || 'undecided'
            }));
            }
        } finally {
            setLoading(false);
        }
        };

    if (user) {
      loadUserProfile();
    } else {
      console.log('No user in auth context');
      setLoading(false);
      setErrors({ general: 'Please log in to view your profile.' });
      setTimeout(() => navigate('/login'), 2000);
    }
  }, [user, navigate]);

  // Handle input changes
  const handleInputChange = (field, value, section = null, index = null) => {
    if (section && index !== null) {
      setProfileData(prev => ({
        ...prev,
        [section]: prev[section].map((item, i) => 
          i === index ? { ...item, [field]: value } : item
        )
      }));
    } else {
      setProfileData(prev => ({
        ...prev,
        [field]: value
      }));
    }
    
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Add new entry to array fields
  const addEntry = (section) => {
    const newEntry = getNewEntryTemplate(section);
    setProfileData(prev => ({
      ...prev,
      [section]: [...prev[section], newEntry]
    }));
  };

  // Remove entry from array fields
  const removeEntry = (section, index) => {
    console.log(`Removing ${section} at index ${index}`); // Add this
    setProfileData(prev => ({
      ...prev,
      [section]: prev[section].filter((_, i) => i !== index)
    }));
  };

  // Get template for new entries
  const getNewEntryTemplate = (section) => {
    switch (section) {
      case 'education':
        return {
          id: `temp-${Date.now()}`,
          institution_name: '',
          degree_level: '',
          degree_name: '',
          major_subject: '',
          start_year: '',
          end_year: '',
          is_current: false,
          grade_system: '',
          overall_grade: '',
          gpa_score: '',
          gpa_scale: '',
          honors: '',
          thesis_title: '',
          institution_city: '',
          institution_country: '',
          institution_state_province: '',
          institution_address: '',
          institution_postal_code: ''
        };
      case 'subjects': // NEW CASE
        return {
          id: `temp-${Date.now()}`,
          education_id: '', // Link to education entry
          subject_name: '',
          grade: '',
          grade_type: 'achieved', // achieved, predicted, target
          priority_order: 1
        };
      case 'experiences':
        return {
          id: `temp-${Date.now()}`,
          company_name: '',
          job_title: '',
          start_date: '',
          end_date: '',
          is_current: false
        };
      case 'skills':
        return {
          id: `temp-${Date.now()}`,
          skill_category: 'technical',
          skill_name: '',
          proficiency_level: 'beginner'
        };
      case 'languages':
        return {
          id: `temp-${Date.now()}`,
          language_name: '',
          speaking_level: 'basic',
          writing_level: 'basic',
          reading_level: 'basic',
          is_native: false
        };
      default:
        return {};
    }
  };

  // Save profile data
const handleSave = async () => {
  try {
    setSaving(true);
    setErrors({});
    setSuccess('');

    const token = localStorage.getItem('pathbuddy_access_token') || sessionStorage.getItem('pathbuddy_access_token');

    if (!token) {
      setErrors({ general: 'Authentication required. Please log in again.' });
      setTimeout(() => navigate('/login'), 2000);
      return;
    }

    console.log('Saving profile data:', profileData);

    // Get the ORIGINAL profile to detect changes
    const backendUrl = getLnatSafeApiBase('https://hammerhead-app-8cr38.ondigitalocean.app');
    const profileUrl = `${backendUrl}/api/auth/profile?_t=${Date.now()}`;
    
    let originalProfile = {};
    try {
      const originalResponse = await fetch(profileUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (originalResponse.ok) {
        originalProfile = await originalResponse.json();
      }
    } catch (e) {
      console.warn('Could not fetch original profile for comparison');
    }

    // Check if subject or Oxbridge status changed
    const subjectChanged = originalProfile.primary_subject !== profileData.primary_subject;
    const oxbridgeChanged = originalProfile.is_oxbridge_candidate !== profileData.is_oxbridge_candidate;
    const degreeApprenticeshipChanged =
      originalProfile.is_degree_apprenticeship_candidate !== profileData.is_degree_apprenticeship_candidate;
    
    // Prepare data to send
    let dataToSend = { ...profileData };
    
    // If subject or Oxbridge status changed, clear the timeline
    // This forces frontend to reload with proper Oxbridge merging
    if (subjectChanged || oxbridgeChanged || degreeApprenticeshipChanged) {
      console.log('Subject or Oxbridge status changed, clearing timeline for reload', {
        subjectChanged,
        oxbridgeChanged,
        degreeApprenticeshipChanged,
        from: {
          subject: originalProfile.primary_subject,
          oxbridge: originalProfile.is_oxbridge_candidate,
          degree_apprenticeship: originalProfile.is_degree_apprenticeship_candidate
        },
        to: {
          subject: profileData.primary_subject,
          oxbridge: profileData.is_oxbridge_candidate,
          degree_apprenticeship: profileData.is_degree_apprenticeship_candidate
        }
      });
      dataToSend.timeline = undefined; // Clear timeline
    }

    const fullApiUrl = `${backendUrl}/api/auth/profile`;
    
    console.log('Making request to BACKEND:', fullApiUrl);

    try {
      const response = await fetchWithRefresh(fullApiUrl, {
        method: 'PUT',
        body: JSON.stringify(dataToSend),
      });

      console.log('Backend response status:', response.status);

      if (response.ok) {
        const result = await response.json();
        console.log('Profile saved successfully:', result);
        
        setSuccess('Profile updated successfully!');
        
        // Clear cached profile
        sessionStorage.removeItem('user_profile');
        
        // Update auth context with the fresh data (include subject so /apps layout syncs)
        updateUser({
          first_name: profileData.first_name,
          last_name: profileData.last_name,
          name: `${profileData.first_name} ${profileData.last_name}`.trim() || user?.email?.split('@')[0] || 'User',
          bio: profileData.bio,
          primary_subject: profileData.primary_subject,
          student_level: profileData.student_level,
        });

        setTimeout(() => setSuccess(''), 5000);
        
        // If navigating to dashboard after save, it will load fresh timeline
      } else {
        const errorText = await response.text();
        console.error('Backend save error response:', errorText);
        
        let errorData = {};
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          console.warn('Could not parse error response as JSON');
          errorData = { error: `Server error (${response.status})` };
        }
        
        if (response.status === 401) {
          setErrors({ general: 'Session expired. Please log in again.' });
          navigate('/login');
        } else {
          setErrors({ general: errorData.error || `Failed to save profile (${response.status})` });
        }
      }
    } catch (networkError) {
      console.error('Network error during save:', networkError);
      setErrors({ general: 'Network error. Please check your connection and try again.' });
    }
  } catch (error) {
    console.error('Unexpected error saving profile:', error);
    setErrors({ general: 'An unexpected error occurred. Please try again.' });
  } finally {
    setSaving(false);
  }
};

const reloadProfileFromDatabase = async () => {
  try {
    const token = localStorage.getItem('pathbuddy_access_token') || sessionStorage.getItem('pathbuddy_access_token');
    
    if (!token) {
      console.warn('No token available for reload');
      return;
    }

    const backendUrl = getLnatSafeApiBase('https://hammerhead-app-8cr38.ondigitalocean.app');
    const apiUrl = `${backendUrl}/api/auth/profile`;
    
    console.log('Reloading profile from:', apiUrl);

    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (response.ok) {
      const freshData = await response.json();
      console.log('Fresh profile data loaded:', freshData);
      console.log('Subjects in fresh data:', freshData.subjects);

      // Update the form state with fresh database data
      setProfileData(prev => ({
        ...prev,
        ...freshData,
        // Ensure arrays exist and handle undefined values
        education: Array.isArray(freshData.education) ? freshData.education : [],
        experiences: Array.isArray(freshData.experiences) ? freshData.experiences : [],
        skills: Array.isArray(freshData.skills) ? freshData.skills : [],
        languages: Array.isArray(freshData.languages) ? freshData.languages : [],
        // Handle empty strings and null values
        first_name: freshData.first_name || '',
        last_name: freshData.last_name || '',
        dob: freshData.dob || '',
        bio: freshData.bio || '',
        phone: freshData.phone || '',
        linkedin_url: freshData.linkedin_url || '',
        personal_website: freshData.personal_website || '',
        professional_summary: freshData.professional_summary || '',
        address_line_1: freshData.address_line_1 || '',
        address_line_2: freshData.address_line_2 || '',
        city: freshData.city || '',
        state_province: freshData.state_province || '',
        postal_code: freshData.postal_code || '',
        country: freshData.country || '',
        student_level: freshData.student_level || 'undecided',
        primary_subject: freshData.primary_subject || 'undecided'
      }));
      
      console.log('Form state updated with fresh data');
    } else {
      console.error('Failed to reload profile data:', response.status);
    }
  } catch (error) {
    console.error('Error reloading profile:', error);
  }
};

  // Password change handlers
  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
    if (passwordErrors[field]) {
      setPasswordErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

const handlePasswordSubmit = async (e) => {
  e.preventDefault();
  setPasswordErrors({});
  setPasswordSuccess('');

  // Validation
  const errors = {};
  if (!passwordData.currentPassword) {
    errors.currentPassword = 'Current password is required';
  }
  if (!passwordData.newPassword) {
    errors.newPassword = 'New password is required';
  } else if (passwordData.newPassword.length < 8) {
    errors.newPassword = 'Password must be at least 8 characters';
  }
  if (passwordData.newPassword !== passwordData.confirmPassword) {
    errors.confirmPassword = 'Passwords do not match';
  }

  if (Object.keys(errors).length > 0) {
    setPasswordErrors(errors);
    return;
  }

  try {
    setPasswordSaving(true);
    const token = localStorage.getItem('pathbuddy_access_token') || sessionStorage.getItem('pathbuddy_access_token');

    // FORCE BACKEND URL - Same pattern as profile save
    const backendUrl = getLnatSafeApiBase('https://hammerhead-app-8cr38.ondigitalocean.app');
    const passwordApiUrl = `${backendUrl}/api/auth/profile/password`;
    
    console.log('Making password change request to BACKEND:', passwordApiUrl);
    console.log('Request data:', {
      current_password: '***',
      new_password: '***'
    });

    const response = await fetchWithRefresh(passwordApiUrl, {
      method: 'PUT',
      body: JSON.stringify({
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      })
    });

    console.log('Password change response status:', response.status);
    console.log('Password change response headers:', Object.fromEntries(response.headers.entries()));

    if (response.ok) {
      const result = await response.json();
      console.log('Password changed successfully:', result);
      
      setPasswordSuccess('Password changed successfully!');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => setPasswordSuccess(''), 3000);
    } else {
      const errorText = await response.text();
      console.error('Password change error response:', errorText);
      
      let errorData = {};
      try {
        errorData = JSON.parse(errorText);
      } catch (e) {
        console.warn('Could not parse password change error response as JSON');
        errorData = { error: `Password change failed (${response.status})` };
      }
      
      if (response.status === 401) {
        setPasswordErrors({ general: 'Session expired. Please log in again.' });
      } else if (response.status === 400 && errorData.error && errorData.error.includes('Current password')) {
        setPasswordErrors({ currentPassword: 'Current password is incorrect' });
      } else {
        setPasswordErrors({ general: errorData.error || 'Failed to change password' });
      }
    }
  } catch (networkError) {
    console.error('Network error during password change:', networkError);
    
    if (networkError.name === 'TypeError' && networkError.message.includes('fetch')) {
      setPasswordErrors({ general: 'Unable to connect to backend. Please check your connection.' });
    } else if (networkError.name === 'AbortError') {
      setPasswordErrors({ general: 'Request timed out. Please try again.' });
    } else {
      setPasswordErrors({ general: 'Network error. Please check your connection and try again.' });
    }
  } finally {
    setPasswordSaving(false);
  }
};

  if (loading) {
    return (
      <div className="profile-page">
        <div className="profile-loading">
          <div className="loading-spinner"></div>
          <p>Loading your profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      {/* Header */}
      <div className="profile-page-header">
        <div className="container">
          <div className="header-content">
            <div className="header-info">
              <h1>Profile Settings</h1>
              <p>Update your personal information and preferences</p>
            </div>

            <div className="header-actions">
              {activeSection !== 'tracker' && (
              <button 
                className="save-button"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <div className="loading-spinner small"></div>
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Floating Popup Messages on Top */}
      {(success || errors.general) && (
        <div className="profile-toast-container">
          {success && (
            <div className="success-message">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="currentColor" strokeWidth="2"/>
                <path d="M22 4L12 14.01l-3-3" stroke="currentColor" strokeWidth="2"/>
              </svg>
              <span>{success}</span>
              <button 
                type="button" 
                onClick={() => setSuccess('')} 
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: '0 0 0 8px', fontSize: '16px', lineHeight: 1 }}
              >
                ×
              </button>
            </div>
          )}

          {errors.general && (
            <div className="error-message">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                <line x1="15" y1="9" x2="9" y2="15" stroke="currentColor" strokeWidth="2"/>
                <line x1="9" y1="9" x2="15" y2="15" stroke="currentColor" strokeWidth="2"/>
              </svg>
              <span>{errors.general}</span>
              <button 
                type="button" 
                onClick={() => setErrors(prev => ({ ...prev, general: '' }))} 
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: '0 0 0 8px', fontSize: '16px', lineHeight: 1 }}
              >
                ×
              </button>
            </div>
          )}
        </div>
      )}

      {/* Section Navigation */}
      <div className="profile-navigation">
        <div className="container">
          <div className="nav-tabs">
            {sections.map((section, index) => (
              <button
                key={section.id}
                data-tour={`profile-nav-${section.id}`}
                className={`nav-tab ${activeSection === section.id ? 'active' : ''}`}
                onClick={() => handleSectionChange(section.id)}
              >
                <span className="tab-label">{section.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="profile-main">
        <div className="container">
          <div className="profile-content">
            
            {/* Personal Information Section */}
            {activeSection === 'personal' && (
              <PersonalSection 
                data={profileData} 
                onChange={handleInputChange}
                errors={errors}
              />
            )}

            {/* Education Section */}
            {activeSection === 'education' && (
              <EducationSection 
                data={profileData} 
                onChange={handleInputChange}
                onAdd={() => addEntry('education')}
                onRemove={(index) => removeEntry('education', index)}
                errors={errors}
              />
            )}

            {/* Subjects Section */}
            {activeSection === 'subjects' && (
              <SubjectsSection 
                data={profileData} 
                onChange={handleInputChange}
                onAdd={() => addEntry('subjects')}
                onRemove={(index) => removeEntry('subjects', index)}
                errors={errors}
              />
            )}

            {/* Experience Section */}
            {activeSection === 'experience' && (
              <ExperienceSection 
                data={profileData} 
                onChange={handleInputChange}
                onAdd={() => addEntry('experiences')}
                onRemove={(index) => removeEntry('experiences', index)}
                errors={errors}
              />
            )}

            {/* Skills Section */}
            {activeSection === 'skills' && (
              <SkillsSection 
                data={profileData} 
                onChange={handleInputChange}
                onAdd={() => addEntry('skills')}
                onRemove={(index) => removeEntry('skills', index)}
                errors={errors}
              />
            )}

            {/* Languages Section */}
            {activeSection === 'languages' && (
              <LanguagesSection 
                data={profileData} 
                onChange={handleInputChange}
                onAdd={() => addEntry('languages')}
                onRemove={(index) => removeEntry('languages', index)}
                errors={errors}
              />
            )}

            {/* Target schools Section */}
            {activeSection === 'target_schools' && (
              <TargetSchoolsSection
                data={profileData}
                setData={setProfileData}
              />
            )}

            {activeSection === 'groups' && <MyOrganisationPanel />}

            {activeSection === 'tracker' && (
              <TrackerPage embedded />
            )}

            {/* Certificates & Credentials Section */}
            {activeSection === 'certificates' && (
              <ProfileCertificatesSection userId={user?.id} />
            )}

            {/* Billing & Subscription Section (hidden on pathbuddy.com) */}
            {activeSection === 'billing' && !hideBilling && (
              <BillingSection user={user} />
            )}

            {/* Security Section */}
            {activeSection === 'security' && (
              <SecuritySection 
                passwordData={passwordData}
                passwordErrors={passwordErrors}
                passwordSaving={passwordSaving}
                passwordSuccess={passwordSuccess}
                showPasswords={showPasswords}
                onPasswordChange={handlePasswordChange}
                onPasswordSubmit={handlePasswordSubmit}
                onTogglePasswordVisibility={togglePasswordVisibility}
              />
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

// Section Components
const PersonalSection = ({ data, onChange, errors }) => {
  // Get available colleges based on selected Oxbridge university
  const getAvailableColleges = (oxbridgeUniversity) => {
    const OXFORD_COLLEGES = [
      'All Souls College', 'Balliol College', 'Brasenose College', 'Christ Church',
      'Corpus Christi College', 'Exeter College', 'Green Templeton College',
      'Harris Manchester College', 'Hertford College', 'Jesus College', 'Keble College',
      'Kellogg College', 'Lady Margaret Hall', 'Linacre College', 'Lincoln College',
      'Magdalen College', 'Mansfield College', 'Merton College', 'New College',
      'Nuffield College', 'Oriel College', 'Pembroke College', 'The Queen\'s College',
      'Reuben College', 'Somerville College', 'St Anne\'s College', 'St Antony\'s College',
      'St Catherine\'s College', 'St Cross College', 'St Edmund Hall', 'St Hilda\'s College',
      'St Hugh\'s College', 'St John\'s College', 'St Peter\'s College', 'Trinity College',
      'University College', 'Wadham College', 'Wolfson College', 'Worcester College'
    ];

    const CAMBRIDGE_COLLEGES = [
      'Christ\'s College', 'Churchill College', 'Clare College', 'Clare Hall',
      'Corpus Christi College', 'Darwin College', 'Downing College', 'Emmanuel College',
      'Fitzwilliam College', 'Girton College', 'Gonville & Caius College', 'Homerton College',
      'Hughes Hall', 'Jesus College', 'King\'s College', 'Lucy Cavendish College',
      'Magdalene College', 'Murray Edwards College', 'Newnham College', 'Pembroke College',
      'Peterhouse', 'Queens\' College', 'Robinson College', 'Selwyn College',
      'Sidney Sussex College', 'St Catharine\'s College', 'St Edmund\'s College',
      'St John\'s College', 'Trinity College', 'Trinity Hall', 'Wolfson College'
    ];

    if (oxbridgeUniversity === 'oxford') return OXFORD_COLLEGES;
    if (oxbridgeUniversity === 'cambridge') return CAMBRIDGE_COLLEGES;
    return [];
  };

  return (
    <div className="profile-section">
      <div className="section-header">
        <div className="section-header-info">
          <h2>Personal Information</h2>
          <p>Your basic information and contact details</p>
        </div>
      </div>

      <div className="form-grid">
        {/* Avatar picker — hidden for now, using initials-based avatar
        <div className="form-group full-width">
          <label style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: '8px', display: 'block' }}>Profile Avatar & Photo</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', marginTop: '6px' }}>
            <div style={{ width: '64px', height: '64px', borderRadius: '50%', border: '2px solid var(--iv-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', background: 'var(--iv-bg)' }}>
              {getAvatarSvg(data.avatar_url || data.avatar, 64)}
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
              {AVATAR_PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => onChange('avatar_url', preset.id)}
                  style={{
                    border: (data.avatar_url === preset.id || data.avatar === preset.id) ? '2px solid var(--iv-accent)' : '1px solid var(--iv-line)',
                    borderRadius: '50%',
                    padding: '2px',
                    background: 'var(--iv-surface)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  title={preset.name}
                >
                  <img src={preset.path} alt={preset.name} style={{ width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover' }} />
                </button>
              ))}
            </div>
          </div>
        </div>
        */}

        <div className="form-group">
          <label>First Name</label>
          <input
            type="text"
            value={data.first_name}
            onChange={(e) => onChange('first_name', e.target.value)}
            placeholder="Enter your first name"
          />
        </div>

        <div className="form-group">
          <label>Last Name</label>
          <input
            type="text"
            value={data.last_name}
            onChange={(e) => onChange('last_name', e.target.value)}
            placeholder="Enter your last name"
          />
        </div>

        {/* Date of Birth — hidden for now
        <div className="form-group">
          <label>Date of Birth</label>
          <input
            type="date"
            value={data.dob}
            onChange={(e) => onChange('dob', e.target.value)}
          />
        </div>
        */}

        {/* Phone — hidden for now
        <div className="form-group">
          <label>Phone</label>
          <input
            type="tel"
            value={data.phone}
            onChange={(e) => onChange('phone', e.target.value)}
            placeholder="Enter your phone number"
          />
        </div>
        */}

        <div className="form-group">
          <label>Creator Profile Name (Slug)</label>
          <input
            type="text"
            value={data.creator_slug}
            onChange={(e) => onChange('creator_slug', e.target.value)}
            placeholder={data.first_name || data.last_name ? `e.g. ${(data.first_name || 'firstname').toLowerCase()}.${(data.last_name || 'lastname').toLowerCase()}` : 'e.g. firstname.lastname'}
          />
          <small style={{ color: 'var(--iv-muted)', marginTop: 4, display: 'block', fontSize: '0.75rem' }}>
            Public Link: <code>{EDU_CANONICAL_BASE}/creator/{data.creator_slug || `${(data.first_name || 'firstname').toLowerCase()}.${(data.last_name || 'lastname').toLowerCase()}`}</code>
          </small>
        </div>

        <div className="form-group full-width">
          <label>Bio</label>
          <textarea
            value={data.bio}
            onChange={(e) => onChange('bio', e.target.value)}
            placeholder="Tell us about yourself..."
            rows="4"
          />
        </div>

        <div className="form-group">
          <label>LinkedIn Profile</label>
          <input
            type="url"
            value={data.linkedin_url}
            onChange={(e) => onChange('linkedin_url', e.target.value)}
            placeholder="https://linkedin.com/in/your-profile"
          />
        </div>

        {/* Personal Website — hidden for now
        <div className="form-group">
          <label>Personal Website</label>
          <input
            type="url"
            value={data.personal_website}
            onChange={(e) => onChange('personal_website', e.target.value)}
            placeholder="https://your-website.com"
          />
        </div>
        */}

        <div className="form-group">
          <label>Student Level</label>
          <select
            value={data.student_level}
            onChange={(e) => onChange('student_level', e.target.value)}
          >
            <option value="undecided">Undecided</option>
            <option value="gcse">GCSE</option>
            <option value="a_level_y12">A-Level Year 12</option>
            <option value="a_level_y13">A-Level Year 13</option>
            <option value="university">University</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="primary_subject">Primary Subject</label>
          <select
            id="primary_subject"
            name="primary_subject"
            value={data.primary_subject}
            onChange={(e) => onChange('primary_subject', e.target.value)}
            className="form-select"
          >
            <option value="undecided">Undecided</option>
            <option value="Investment Banking">Investment Banking</option>
            <option value="Computer Science">Computer Science</option>
            <option value="Engineering">Engineering</option>
            <option value="Law">Law</option>
            <option value="Medicine">Medicine</option>
            <option value="Geography">Geography</option>
            <option value="Management Consulting">Management Consulting</option>
          </select>
        </div>

        <RedoOnboardingButton />
      </div>
      
      {/* Pathway Preferences */}
      <div className="subsection" data-tour="path-update">
        <h3>Pathway Preferences</h3>
        <div className="form-grid">
          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={data.is_oxbridge_candidate || false}
                onChange={(e) => {
                  onChange('is_oxbridge_candidate', e.target.checked);
                  // Clear Oxbridge fields if unchecked
                  if (!e.target.checked) {
                    onChange('oxbridge_university', '');
                    onChange('oxbridge_college', '');
                  }
                }}
              />
              I am an Oxbridge candidate
            </label>
            <small style={{ color: 'var(--iv-muted)', fontSize: '12px', display: 'block', marginTop: '4px' }}>
              Check this if you're targeting Oxford or Cambridge University
            </small>
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={data.is_degree_apprenticeship_candidate || false}
                onChange={(e) => {
                  onChange('is_degree_apprenticeship_candidate', e.target.checked);
                }}
              />
              I am targeting a Degree Apprenticeship
            </label>
            <small style={{ color: 'var(--iv-muted)', fontSize: '12px', display: 'block', marginTop: '4px' }}>
              Keep this selected to include degree apprenticeship-focused tasks alongside your subject path
            </small>
          </div>

          {data.is_oxbridge_candidate && (
            <>
              <div className="form-group">
                <label>Oxbridge University</label>
                <CustomSelect
                  value={data.oxbridge_university || ''}
                  onChange={(val) => {
                    onChange('oxbridge_university', val);
                    // Clear college selection when university changes
                    onChange('oxbridge_college', '');
                  }}
                  options={[
                    { value: 'oxford', label: 'University of Oxford' },
                    { value: 'cambridge', label: 'University of Cambridge' }
                  ]}
                  placeholder="Select University"
                />
              </div>

              {data.oxbridge_university && (
                <div className="form-group">
                  <label>College</label>
                  <CustomSelect
                    value={data.oxbridge_college || ''}
                    onChange={(val) => onChange('oxbridge_college', val)}
                    options={getAvailableColleges(data.oxbridge_university).map((college) => ({
                      value: college,
                      label: college
                    }))}
                    placeholder="Select College"
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="subsection">
        <h3>Address Information</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>Address Line 1</label>
            <input
              type="text"
              value={data.address_line_1}
              onChange={(e) => onChange('address_line_1', e.target.value)}
              placeholder="Street address"
            />
          </div>

          <div className="form-group">
            <label>Address Line 2</label>
            <input
              type="text"
              value={data.address_line_2}
              onChange={(e) => onChange('address_line_2', e.target.value)}
              placeholder="Apartment, suite, etc. (optional)"
            />
          </div>

          <div className="form-group">
            <label>City</label>
            <input
              type="text"
              value={data.city}
              onChange={(e) => onChange('city', e.target.value)}
              placeholder="Enter your city"
            />
          </div>

          <div className="form-group">
            <label>State/Province</label>
            <input
              type="text"
              value={data.state_province}
              onChange={(e) => onChange('state_province', e.target.value)}
              placeholder="State or province"
            />
          </div>

          <div className="form-group">
            <label>Postal Code</label>
            <input
              type="text"
              value={data.postal_code}
              onChange={(e) => onChange('postal_code', e.target.value)}
              placeholder="Postal/ZIP code"
            />
          </div>

          <div className="form-group">
            <label>Country</label>
            <input
              type="text"
              value={data.country}
              onChange={(e) => onChange('country', e.target.value)}
              placeholder="Enter your country"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const EducationSection = ({ data, onChange, onAdd, onRemove }) => (
  <div className="profile-section" data-tour="profile-education">
    <div className="section-header">
      <div className="section-header-info">
        <h2>Education</h2>
        <p>Your academic background and qualifications</p>
      </div>
      <button className="add-button" onClick={onAdd}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
          <path d="M12 8v8M8 12h8" stroke="currentColor" strokeWidth="2"/>
        </svg>
        Add Education
      </button>
    </div>

    {data.education.length === 0 ? (
      <div className="empty-state">
        <p>No education entries yet. Add your educational background to build your profile.</p>
      </div>
    ) : (
      data.education.map((edu, index) => (
        <div key={edu.id || index} className="entry-card">
          <div className="entry-header">
            <h4>Education {index + 1}</h4>
            <button onClick={() => onRemove(index)} className="remove-button">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14z" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </button>
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label>Institution Name</label>
              <input
                type="text"
                value={edu.institution_name || ''}
                onChange={(e) => onChange('institution_name', e.target.value, 'education', index)}
                placeholder="University/School name"
              />
            </div>

            <div className="form-group">
              <label>Degree Level</label>
              <select
                value={edu.degree_level || ''}
                onChange={(e) => onChange('degree_level', e.target.value, 'education', index)}
              >
                <option value="">Select level</option>
                <option value="gcse">GCSE</option>
                <option value="a_level">A-Level</option>
                <option value="bachelor">Bachelor's</option>
                <option value="master">Master's</option>
                <option value="phd">PhD</option>
                <option value="diploma">Diploma</option>
              </select>
            </div>

            <div className="form-group">
              <label>Degree/Course Name</label>
              <input
                type="text"
                value={edu.degree_name || ''}
                onChange={(e) => onChange('degree_name', e.target.value, 'education', index)}
                placeholder="e.g., Computer Science BSc"
              />
              <small style={{ color: 'var(--iv-muted)', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                Full degree title - useful for detailed profiles and future CV enhancements
              </small>
            </div>

            <div className="form-group">
              <label>Major/Subject</label>
              <input
                type="text"
                value={edu.major_subject || ''}
                onChange={(e) => onChange('major_subject', e.target.value, 'education', index)}
                placeholder="e.g., Computer Science, Economics"
              />
            </div>

            <div className="form-group">
              <label>Institution City</label>
              <input
                type="text"
                value={edu.institution_city || ''}
                onChange={(e) => onChange('institution_city', e.target.value, 'education', index)}
                placeholder="City where institution is located"
              />
            </div>

            <div className="form-group">
              <label>Institution Country</label>
              <input
                type="text"
                value={edu.institution_country || ''}
                onChange={(e) => onChange('institution_country', e.target.value, 'education', index)}
                placeholder="Country where institution is located"
              />
            </div>

            <div className="form-group">
              <label>Institution State/Province</label>
              <input
                type="text"
                value={edu.institution_state_province || ''}
                onChange={(e) => onChange('institution_state_province', e.target.value, 'education', index)}
                placeholder="State or province (if applicable)"
              />
            </div>

            <div className="form-group">
              <label>Institution Address</label>
              <input
                type="text"
                value={edu.institution_address || ''}
                onChange={(e) => onChange('institution_address', e.target.value, 'education', index)}
                placeholder="Full address of institution"
              />
            </div>

            <div className="form-group">
              <label>Institution Postal Code</label>
              <input
                type="text"
                value={edu.institution_postal_code || ''}
                onChange={(e) => onChange('institution_postal_code', e.target.value, 'education', index)}
                placeholder="Postal/ZIP code"
              />
            </div>

            <div className="form-group">
              <label>Start Year</label>
              <input
                type="number"
                value={edu.start_year || ''}
                onChange={(e) => onChange('start_year', e.target.value, 'education', index)}
                placeholder="2020"
                min="1950"
                max="2030"
              />
            </div>

            <div className="form-group">
              <label>End Year</label>
              <input
                type="number"
                value={edu.end_year || ''}
                onChange={(e) => onChange('end_year', e.target.value, 'education', index)}
                placeholder="2024"
                min="1950"
                max="2030"
                disabled={edu.is_current}
              />
            </div>

            <div className="form-group">
              <label>Grade System</label>
              <select
                value={edu.grade_system || ''}
                onChange={(e) => onChange('grade_system', e.target.value, 'education', index)}
              >
                <option value="">Select system</option>
                <option value="gpa">GPA</option>
                <option value="uk_degree">UK Degree Classification</option>
                <option value="a_level_letters">A-Level Letters</option>
                <option value="gcse_numbers">GCSE Numbers</option>
                <option value="percentage">Percentage</option>
              </select>
            </div>

            <div className="form-group">
              <label>Overall Grade/GPA</label>
              <input
                type="text"
                value={edu.overall_grade || ''}
                onChange={(e) => onChange('overall_grade', e.target.value, 'education', index)}
                placeholder="e.g., 3.8, First Class, A*"
              />
            </div>

            <div className="form-group">
              <label>GPA Score</label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="5"
                value={edu.gpa_score || ''}
                onChange={(e) => onChange('gpa_score', e.target.value, 'education', index)}
                placeholder="e.g., 3.8"
              />
            </div>

            <div className="form-group">
              <label>GPA Scale</label>
              <select
                value={edu.gpa_scale || ''}
                onChange={(e) => onChange('gpa_scale', e.target.value, 'education', index)}
              >
                <option value="">Select scale</option>
                <option value="4.0">4.0</option>
                <option value="4.33">4.33</option>
                <option value="5.0">5.0</option>
                <option value="100">100</option>
              </select>
            </div>

            <div className="form-group">
              <label>Honors</label>
              <input
                type="text"
                value={edu.honors || ''}
                onChange={(e) => onChange('honors', e.target.value, 'education', index)}
                placeholder="e.g., Magna Cum Laude, First Class Honours"
              />
            </div>

            <div className="form-group">
              <label>Thesis Title</label>
              <input
                type="text"
                value={edu.thesis_title || ''}
                onChange={(e) => onChange('thesis_title', e.target.value, 'education', index)}
                placeholder="For advanced degrees (Master's, PhD)"
              />
            </div>

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={edu.is_current || false}
                  onChange={(e) => {
                    onChange('is_current', e.target.checked, 'education', index);
                    if (e.target.checked) {
                      onChange('end_year', '', 'education', index);
                    }
                  }}
                />
                Currently studying here
              </label>
            </div>
          </div>
        </div>
      ))
    )}
  </div>
);

const ExperienceSection = ({ data, onChange, onAdd, onRemove }) => (
  <div className="profile-section">
    <div className="section-header">
      <div className="section-header-info">
        <h2>Work Experience</h2>
        <p>Your professional experience and work history</p>
      </div>
      <button className="add-button" onClick={onAdd}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
          <path d="M12 8v8M8 12h8" stroke="currentColor" strokeWidth="2"/>
        </svg>
        Add Experience
      </button>
    </div>

    {data.experiences.length === 0 ? (
      <div className="empty-state">
        <p>No work experience added yet. Add your professional experience to showcase your background.</p>
      </div>
    ) : (
      data.experiences.map((exp, index) => (
        <div key={exp.id || index} className="entry-card">
          <div className="entry-header">
            <h4>Experience {index + 1}</h4>
            <button onClick={() => onRemove(index)} className="remove-button">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14z" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </button>
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label>Company Name</label>
              <input
                type="text"
                value={exp.company_name || ''}
                onChange={(e) => onChange('company_name', e.target.value, 'experiences', index)}
                placeholder="Company or organization"
              />
            </div>

            <div className="form-group">
              <label>Job Title</label>
              <input
                type="text"
                value={exp.job_title || ''}
                onChange={(e) => onChange('job_title', e.target.value, 'experiences', index)}
                placeholder="Your role or position"
              />
            </div>

            <div className="form-group">
              <label>Group/Division</label>
              <input
                type="text"
                value={exp.group_division || ''}
                onChange={(e) => onChange('group_division', e.target.value, 'experiences', index)}
                placeholder="e.g., M&A, Sales & Trading"
              />
            </div>

            <div className="form-group">
              <label>City</label>
              <input
                type="text"
                value={exp.company_city || ''}
                onChange={(e) => onChange('company_city', e.target.value, 'experiences', index)}
                placeholder="City where you worked"
              />
            </div>

            <div className="form-group">
              <label>Country</label>
              <input
                type="text"
                value={exp.company_country || ''}
                onChange={(e) => onChange('company_country', e.target.value, 'experiences', index)}
                placeholder="Country where you worked"
              />
            </div>

            <div className="form-group">
              <label>State/Province</label>
              <input
                type="text"
                value={exp.state_province || ''}
                onChange={(e) => onChange('state_province', e.target.value, 'experiences', index)}
                placeholder="State or province"
              />
            </div>

            <div className="form-group">
              <label>Start Date</label>
              <input
                type="date"
                value={exp.start_date || ''}
                onChange={(e) => onChange('start_date', e.target.value, 'experiences', index)}
              />
            </div>

            <div className="form-group">
              <label>End Date</label>
              <input
                type="date"
                value={exp.end_date || ''}
                onChange={(e) => onChange('end_date', e.target.value, 'experiences', index)}
                disabled={exp.is_current}
              />
            </div>

            <div className="form-group checkbox-group full-width">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={exp.is_current || false}
                  onChange={(e) => {
                    onChange('is_current', e.target.checked, 'experiences', index);
                    if (e.target.checked) {
                      onChange('end_date', '', 'experiences', index);
                    }
                  }}
                />
                Currently working here
              </label>
            </div>

            <div className="form-group full-width">
              <label>Key Achievements</label>
              <textarea
                value={Array.isArray(exp.achievements) ? exp.achievements.join('\n') : (exp.achievements || '')}
                onChange={(e) => {
                  const achievementsArray = e.target.value.split('\n').filter(line => line.trim());
                  onChange('achievements', achievementsArray, 'experiences', index);
                }}
                placeholder="• Led project that increased efficiency by 25%
• Managed team of 5 developers
• Delivered project 2 weeks ahead of schedule"
                rows="4"
              />
              <small style={{ color: 'var(--iv-muted)', fontSize: '12px' }}>
                Enter each achievement on a new line. This will auto-populate your CV forms.
              </small>
            </div>
          </div>
        </div>
      ))
    )}
  </div>
);

const SubjectsSection = ({ data, onChange, onAdd, onRemove }) => (
  <div className="profile-section">
    <div className="section-header">
      <div className="section-header-info">
        <h2>Subjects & Grades</h2>
        <p>Individual subjects with grades - essential for A-level students, optional for others</p>
      </div>
      <button className="add-button" onClick={onAdd}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
          <path d="M12 8v8M8 12h8" stroke="currentColor" strokeWidth="2"/>
        </svg>
        Add Subject
      </button>
    </div>

    {data.subjects.length === 0 ? (
      <div className="empty-state">
        <p>No subjects added yet. Add your subjects with grades for CV generation and applications.</p>
      </div>
    ) : (
      data.subjects.map((subject, index) => (
        <div key={subject.id || index} className="entry-card">
          <div className="entry-header">
            <h4>Subject {index + 1}</h4>
            <button onClick={() => onRemove(index)} className="remove-button">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14z" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </button>
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label>Subject Name</label>
              <input
                type="text"
                value={subject.subject_name || ''}
                onChange={(e) => onChange('subject_name', e.target.value, 'subjects', index)}
                placeholder="e.g., Mathematics, Physics, Computer Science"
              />
            </div>

            <div className="form-group">
              <label>Grade</label>
              <input
                type="text"
                value={subject.grade || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  // Validate grade format on change
                  if (value === '' || validateGradeFormat(value)) {
                    onChange('grade', value, 'subjects', index);
                  }
                }}
                placeholder="e.g., A*, A, B, 9, 8, 7, First, 2:1"
              />
              <small style={{ color: 'var(--iv-muted)', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                Valid formats: A*, A, B+ (A-level), 9, 8, 7 (GCSE), First, 2:1 (UK degree)
              </small>
            </div>

            <div className="form-group">
              <label>Grade Type</label>
              <select
                value={subject.grade_type || 'achieved'}
                onChange={(e) => onChange('grade_type', e.target.value, 'subjects', index)}
              >
                <option value="achieved">Achieved</option>
                <option value="predicted">Predicted</option>
                <option value="target">Target</option>
              </select>
            </div>

            <div className="form-group">
              <label>Priority Order</label>
              <select
                value={subject.priority_order || 1}
                onChange={(e) => onChange('priority_order', parseInt(e.target.value), 'subjects', index)}
              >
                <option value="1">1 (Highest)</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4</option>
                <option value="5">5</option>
              </select>
            </div>

            {data.education && data.education.length > 0 && (
              <div className="form-group">
                <label>Link to Education</label>
                <select
                  value={subject.education_id || ''}
                  onChange={(e) => onChange('education_id', e.target.value, 'subjects', index)}
                >
                  <option value="">Select education entry</option>
                  {data.education.map((edu, eduIndex) => (
                    <option key={edu.id || eduIndex} value={edu.id}>
                      {edu.institution_name || `Education ${eduIndex + 1}`} - {edu.degree_level || 'Unknown Level'}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="form-group full-width">
            <small style={{ color: 'var(--iv-muted)', fontSize: '12px' }}>
              <strong>Tip:</strong> For A-level students, add your subjects here to show "Mathematics (A*), Physics (A)" format in CVs.
            </small>
          </div>
        </div>
      ))
    )}
  </div>
);

const SkillsSection = ({ data, onChange, onAdd, onRemove }) => (
  <div className="profile-section">
    <div className="section-header">
      <div className="section-header-info">
        <h2>Skills & Certifications</h2>
        <p>Your technical skills and professional certifications</p>
      </div>
      <button className="add-button" onClick={onAdd}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
          <path d="M12 8v8M8 12h8" stroke="currentColor" strokeWidth="2"/>
        </svg>
        Add Skill
      </button>
    </div>

    {data.skills.length === 0 ? (
      <div className="empty-state">
        <p>No skills added yet. Add your technical skills, certifications, and other abilities.</p>
      </div>
    ) : (
      data.skills.map((skill, index) => (
        <div key={skill.id || index} className="entry-card">
          <div className="entry-header">
            <h4>Skill {index + 1}</h4>
            <button onClick={() => onRemove(index)} className="remove-button">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14z" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </button>
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label>Category</label>
              <select
                value={skill.skill_category || 'technical'}
                onChange={(e) => onChange('skill_category', e.target.value, 'skills', index)}
              >
                <option value="technical">Technical</option>
                <option value="certification">Certification</option>
                <option value="activity">Activity</option>
                <option value="software">Software</option>
                <option value="language_tech">Programming Language</option>
              </select>
            </div>

            <div className="form-group">
              <label>Skill Name</label>
              <input
                type="text"
                value={skill.skill_name || ''}
                onChange={(e) => onChange('skill_name', e.target.value, 'skills', index)}
                placeholder="e.g., Python, AWS Certified, Photography"
              />
            </div>

            <div className="form-group">
              <label>Proficiency Level</label>
              <select
                value={skill.proficiency_level || 'beginner'}
                onChange={(e) => onChange('proficiency_level', e.target.value, 'skills', index)}
              >
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
                <option value="expert">Expert</option>
              </select>
            </div>
          </div>
        </div>
      ))
    )}
  </div>
);

const LanguagesSection = ({ data, onChange, onAdd, onRemove }) => (
  <div className="profile-section">
    <div className="section-header">
      <div className="section-header-info">
        <h2>Languages</h2>
        <p>Languages you speak and your proficiency levels</p>
      </div>
      <button className="add-button" onClick={onAdd}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
          <path d="M12 8v8M8 12h8" stroke="currentColor" strokeWidth="2"/>
        </svg>
        Add Language
      </button>
    </div>

    {data.languages.length === 0 ? (
      <div className="empty-state">
        <p>No languages added yet. Add the languages you speak to showcase your international capabilities.</p>
      </div>
    ) : (
      data.languages.map((lang, index) => (
        <div key={lang.id || index} className="entry-card">
          <div className="entry-header">
            <h4>Language {index + 1}</h4>
            <button onClick={() => onRemove(index)} className="remove-button">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14z" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </button>
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label>Language</label>
              <input
                type="text"
                value={lang.language_name || ''}
                onChange={(e) => onChange('language_name', e.target.value, 'languages', index)}
                placeholder="e.g., English, Spanish, Mandarin"
              />
            </div>

            <div className="form-group">
              <label>Speaking Level</label>
              <select
                value={lang.speaking_level || 'basic'}
                onChange={(e) => onChange('speaking_level', e.target.value, 'languages', index)}
              >
                <option value="basic">Basic</option>
                <option value="conversational">Conversational</option>
                <option value="fluent">Fluent</option>
                <option value="native">Native</option>
              </select>
            </div>

            <div className="form-group">
              <label>Writing Level</label>
              <select
                value={lang.writing_level || 'basic'}
                onChange={(e) => onChange('writing_level', e.target.value, 'languages', index)}
              >
                <option value="basic">Basic</option>
                <option value="conversational">Conversational</option>
                <option value="fluent">Fluent</option>
                <option value="native">Native</option>
              </select>
            </div>

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={lang.is_native || false}
                  onChange={(e) => {
                    const isNative = e.target.checked;
                    onChange('is_native', isNative, 'languages', index);
                    if (isNative) {
                      onChange('speaking_level', 'native', 'languages', index);
                      onChange('writing_level', 'native', 'languages', index);
                    }
                  }}
                />
                Native language
              </label>
            </div>
          </div>
        </div>
      ))
    )}
  </div>
);

const SecuritySection = ({ 
  passwordData, 
  passwordErrors, 
  passwordSaving, 
  passwordSuccess, 
  showPasswords, 
  onPasswordChange, 
  onPasswordSubmit, 
  onTogglePasswordVisibility 
}) => (
  <div className="profile-section">
    <div className="section-header">
      <div className="section-header-info">
        <h2>Security Settings</h2>
        <p>Password and account security options</p>
      </div>
    </div>

    <div className="security-section">
      <h4>Change Password</h4>
      <p className="security-description">
        Update your password to keep your account secure. You'll remain logged in after changing your password.
      </p>

      {passwordSuccess && (
        <div className="success-message">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="currentColor" strokeWidth="2"/>
            <path d="M22 4L12 14.01l-3-3" stroke="currentColor" strokeWidth="2"/>
          </svg>
          {passwordSuccess}
        </div>
      )}

      {passwordErrors.general && (
        <div className="error-message">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
            <line x1="15" y1="9" x2="9" y2="15" stroke="currentColor" strokeWidth="2"/>
            <line x1="9" y1="9" x2="15" y2="15" stroke="currentColor" strokeWidth="2"/>
          </svg>
          {passwordErrors.general}
        </div>
      )}

      <form className="password-change-form" onSubmit={onPasswordSubmit}>
        <div className="form-grid">
          <div className="form-group">
            <label>Current Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPasswords.current ? 'text' : 'password'}
                value={passwordData.currentPassword}
                onChange={(e) => onPasswordChange('currentPassword', e.target.value)}
                placeholder="Enter current password"
                className={passwordErrors.currentPassword ? 'error' : ''}
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => onTogglePasswordVisibility('current')}
              >
                {showPasswords.current ? 
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20C7 20 2.73 16.39 1 12A18.45 18.45 0 0 1 5.06 5.06M9.9 4.24A9.12 9.12 0 0 1 12 4C17 4 21.27 7.61 23 12A18.5 18.5 0 0 1 19.42 16.42" stroke="currentColor" strokeWidth="2"/>
                    <path d="M12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12C10 13.1046 10.8954 14 12 14Z" stroke="currentColor" strokeWidth="2"/>
                    <path d="M1 1L23 23" stroke="currentColor" strokeWidth="2"/>
                  </svg> : 
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M1 12S5 4 12 4S23 12 23 12S19 20 12 20S1 12 1 12Z" stroke="currentColor" strokeWidth="2"/>
                    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                }
              </button>
            </div>
            {passwordErrors.currentPassword && (
              <span className="field-error">{passwordErrors.currentPassword}</span>
            )}
          </div>

          <div className="form-group">
            <label>New Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPasswords.new ? 'text' : 'password'}
                value={passwordData.newPassword}
                onChange={(e) => onPasswordChange('newPassword', e.target.value)}
                placeholder="Enter new password"
                className={passwordErrors.newPassword ? 'error' : ''}
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => onTogglePasswordVisibility('new')}
              >
                {showPasswords.new ? 
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20C7 20 2.73 16.39 1 12A18.45 18.45 0 0 1 5.06 5.06M9.9 4.24A9.12 9.12 0 0 1 12 4C17 4 21.27 7.61 23 12A18.5 18.5 0 0 1 19.42 16.42" stroke="currentColor" strokeWidth="2"/>
                    <path d="M12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12C10 13.1046 10.8954 14 12 14Z" stroke="currentColor" strokeWidth="2"/>
                    <path d="M1 1L23 23" stroke="currentColor" strokeWidth="2"/>
                  </svg> : 
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M1 12S5 4 12 4S23 12 23 12S19 20 12 20S1 12 1 12Z" stroke="currentColor" strokeWidth="2"/>
                    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                }
              </button>
            </div>
            {passwordErrors.newPassword && (
              <span className="field-error">{passwordErrors.newPassword}</span>
            )}
          </div>

          <div className="form-group">
            <label>Confirm New Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPasswords.confirm ? 'text' : 'password'}
                value={passwordData.confirmPassword}
                onChange={(e) => onPasswordChange('confirmPassword', e.target.value)}
                placeholder="Confirm new password"
                className={passwordErrors.confirmPassword ? 'error' : ''}
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => onTogglePasswordVisibility('confirm')}
              >
                {showPasswords.confirm ? 
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20C7 20 2.73 16.39 1 12A18.45 18.45 0 0 1 5.06 5.06M9.9 4.24A9.12 9.12 0 0 1 12 4C17 4 21.27 7.61 23 12A18.5 18.5 0 0 1 19.42 16.42" stroke="currentColor" strokeWidth="2"/>
                    <path d="M12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12C10 13.1046 10.8954 14 12 14Z" stroke="currentColor" strokeWidth="2"/>
                    <path d="M1 1L23 23" stroke="currentColor" strokeWidth="2"/>
                  </svg> : 
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M1 12S5 4 12 4S23 12 23 12S19 20 12 20S1 12 1 12Z" stroke="currentColor" strokeWidth="2"/>
                    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                }
              </button>
            </div>
            {passwordErrors.confirmPassword && (
              <span className="field-error">{passwordErrors.confirmPassword}</span>
            )}
          </div>
        </div>

        <div className="password-form-actions">
          <button 
            type="submit" 
            className="change-password-btn"
            disabled={passwordSaving}
          >
            {passwordSaving ? (
              <>
                <div className="loading-spinner small"></div>
                Changing Password...
              </>
            ) : (
              'Change Password'
            )}
          </button>
        </div>
      </form>
    </div>
  </div>
);

const TargetSchoolsSection = ({ data, setData }) => {
  const targetApps = data.target_applications || [];

  const handleFieldChange = (index, field, value) => {
    setData(prev => {
      const updated = [...(prev.target_applications || [])];
      updated[index] = {
        ...updated[index],
        [field]: value
      };
      return { ...prev, target_applications: updated };
    });
  };

  const handleGradeChange = (appIndex, gradeIndex, field, value) => {
    setData(prev => {
      const updated = [...(prev.target_applications || [])];
      const grades = [...(updated[appIndex].grade_requirements || [])];
      const existing = grades[gradeIndex] || { requirement_order: gradeIndex + 1 };
      grades[gradeIndex] = { ...existing, [field]: value, requirement_order: gradeIndex + 1 };
      updated[appIndex] = { ...updated[appIndex], grade_requirements: grades };
      return { ...prev, target_applications: updated };
    });
  };

  const handleExamToggle = (appIndex, examType) => {
    setData(prev => {
      const updated = [...(prev.target_applications || [])];
      const exams = [...(updated[appIndex].exam_requirements || [])];
      const existingIndex = exams.findIndex(e => e.exam_type === examType);
      if (existingIndex >= 0) {
        exams.splice(existingIndex, 1);
      } else {
        exams.push({
          exam_type: examType,
          is_required: true,
          target_score: '',
          notes: ''
        });
      }
      updated[appIndex] = { ...updated[appIndex], exam_requirements: exams };
      return { ...prev, target_applications: updated };
    });
  };

  const addTargetApplication = () => {
    setData(prev => ({
      ...prev,
      target_applications: [
        ...(prev.target_applications || []),
        {
          id: `temp-${Date.now()}`,
          institution_name: '',
          institution_country: '',
          course_name: '',
          course_code: '',
          study_level: '',
          application_cycle: '',
          rank_order: (prev.target_applications?.length || 0) + 1,
          is_first_choice: false,
          is_insurance_choice: false,
          notes: '',
          grade_requirements: [],
          exam_requirements: []
        }
      ]
    }));
  };

  const removeTargetApplication = (index) => {
    setData(prev => ({
      ...prev,
      target_applications: (prev.target_applications || []).filter((_, i) => i !== index)
    }));
  };

  const examOptions = [
    { value: 'tmua', label: 'TMUA' },
    { value: 'lnat', label: 'LNAT' },
    { value: 'ucat', label: 'UCAT' },
    { value: 'step', label: 'STEP' },
    { value: 'tsa', label: 'TSA' },
    { value: 'tara', label: 'TARA' }
  ];

  const gradeOptions = ['A*', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'U'];

  return (
    <div className="profile-section">
      <div className="section-header">
        <div className="section-header-info">
          <h2>Target schools</h2>
          <p>Define your target universities, required grades and admissions tests.</p>
        </div>
        <button className="add-button" onClick={addTargetApplication}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
            <path d="M12 8v8M8 12h8" stroke="currentColor" strokeWidth="2" />
          </svg>
          Add Target
        </button>
      </div>

      {targetApps.length === 0 ? (
        <div className="empty-state">
          <p>No target schools added yet. Add each university + course you are applying for.</p>
        </div>
      ) : (
        targetApps.map((app, index) => (
          <div key={app.id || index} className="entry-card">
            <div className="entry-header">
              <h4>Target {index + 1}</h4>
              <button onClick={() => removeTargetApplication(index)} className="remove-button">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14z"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                </svg>
              </button>
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label>Institution name</label>
                <input
                  type="text"
                  value={app.institution_name || ''}
                  onChange={(e) => handleFieldChange(index, 'institution_name', e.target.value)}
                  placeholder="University name"
                />
              </div>

              <div className="form-group">
                <label>Course</label>
                <input
                  type="text"
                  value={app.course_name || ''}
                  onChange={(e) => handleFieldChange(index, 'course_name', e.target.value)}
                  placeholder="e.g., Computer Science"
                />
              </div>

              <div className="form-group">
                <label>Application cycle</label>
                <input
                  type="text"
                  value={app.application_cycle || ''}
                  onChange={(e) => handleFieldChange(index, 'application_cycle', e.target.value)}
                  placeholder="e.g., 2026 entry"
                />
              </div>

              <div className="form-group">
                <label>Rank</label>
                <input
                  type="number"
                  min="1"
                  value={app.rank_order || index + 1}
                  onChange={(e) =>
                    handleFieldChange(index, 'rank_order', parseInt(e.target.value, 10) || null)
                  }
                />
              </div>

              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={app.is_first_choice || false}
                    onChange={(e) => handleFieldChange(index, 'is_first_choice', e.target.checked)}
                  />
                  First choice
                </label>
              </div>

              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={app.is_insurance_choice || false}
                    onChange={(e) =>
                      handleFieldChange(index, 'is_insurance_choice', e.target.checked)
                    }
                  />
                  Insurance choice
                </label>
              </div>
            </div>

            <div className="subsection">
              <h3>Target grades</h3>
              <div className="form-grid">
                {[0, 1, 2].map((i) => {
                  const gradeRow = (app.grade_requirements || [])[i] || {};
                  return (
                    <React.Fragment key={i}>
                      <div className="form-group">
                        <label>Subject {i + 1}</label>
                        <input
                          type="text"
                          value={gradeRow.subject_name || ''}
                          onChange={(e) =>
                            handleGradeChange(index, i, 'subject_name', e.target.value)
                          }
                          placeholder="e.g., Mathematics"
                        />
                      </div>
                      <div className="form-group">
                        <label>Required grade</label>
                        <select
                          value={gradeRow.required_grade || ''}
                          onChange={(e) =>
                            handleGradeChange(index, i, 'required_grade', e.target.value)
                          }
                        >
                          <option value="">Select grade</option>
                          {gradeOptions.map((g) => (
                            <option key={g} value={g}>
                              {g}
                            </option>
                          ))}
                        </select>
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>

            <div className="subsection">
              <h3>Entrance exams</h3>
              <div className="form-grid">
                <div className="form-group full-width">
                  <div className="exam-badge-row">
                    {examOptions.map((exam) => {
                      const active =
                        (app.exam_requirements || []).findIndex(
                          (e) => e.exam_type === exam.value
                        ) >= 0;
                      return (
                        <button
                          key={exam.value}
                          type="button"
                          className={`exam-badge ${active ? 'active' : ''}`}
                          onClick={() => handleExamToggle(index, exam.value)}
                        >
                          {exam.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

const CARD_BRAND_ICONS = {
  visa: (
    <svg width="40" height="26" viewBox="0 0 40 26" fill="none"><rect width="40" height="26" rx="4" fill="#1A1F71"/><text x="20" y="16" textAnchor="middle" fill="#fff" fontSize="10" fontWeight="700" fontFamily="sans-serif">VISA</text></svg>
  ),
  mastercard: (
    <svg width="40" height="26" viewBox="0 0 40 26" fill="none"><rect width="40" height="26" rx="4" fill="#252525"/><circle cx="16" cy="13" r="8" fill="#EB001B" opacity="0.9"/><circle cx="24" cy="13" r="8" fill="#F79E1B" opacity="0.9"/></svg>
  ),
  amex: (
    <svg width="40" height="26" viewBox="0 0 40 26" fill="none"><rect width="40" height="26" rx="4" fill="#2E77BC"/><text x="20" y="16" textAnchor="middle" fill="#fff" fontSize="7" fontWeight="700" fontFamily="sans-serif">AMEX</text></svg>
  ),
  default: (
    <svg width="40" height="26" viewBox="0 0 40 26" fill="none"><rect width="40" height="26" rx="4" style={{ fill: 'var(--iv-surface)' }}/><text x="20" y="16" textAnchor="middle" style={{ fill: 'var(--iv-muted)' }} fontSize="8" fontWeight="600" fontFamily="sans-serif">CARD</text></svg>
  ),
};

const BillingSection = ({ user }) => {
  const navigate = useNavigate();
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(true);

  const authHeaders = () => {
    const token = authAPI.getAccessToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchBilling = async () => {
    try {
      setLoading(true);
      const apiUrl = getBaseApiUrl();
      const res = await fetch(`${apiUrl}/api/stripe/billing-details`, {
        headers: { ...authHeaders() },
      });
      if (res.ok) {
        const data = await res.json();
        setBilling(data);
      }
    } catch (err) {
      console.error('Billing details fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      setTransactionsLoading(true);
      const apiUrl = getBaseApiUrl();
      const res = await fetch(`${apiUrl}/api/stripe/transaction-history`, {
        headers: { ...authHeaders() },
      });
      if (res.ok) {
        const data = await res.json();
        setTransactions(Array.isArray(data.transactions) ? data.transactions : []);
      }
    } catch (err) {
      console.error('Transaction history fetch error:', err);
      setTransactions([]);
    } finally {
      setTransactionsLoading(false);
    }
  };

  useEffect(() => {
    fetchBilling();
    fetchTransactions();
  }, [user]);

  const handleOpenStripePortal = async () => {
    try {
      setPortalLoading(true);
      setPortalError('');
      const apiUrl = getBaseApiUrl();
      const res = await fetch(`${apiUrl}/api/stripe/create-portal-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({})
      });
      const data = await res.json();
      if (data?.portalUrl) {
        window.open(data.portalUrl, '_blank');
      } else {
        window.open('https://billing.stripe.com/p/login/4gMbJ36Vpg9Gc4i4rf2B200', '_blank');
      }
    } catch (err) {
      console.error('Portal session error:', err);
      window.open('https://billing.stripe.com/p/login/4gMbJ36Vpg9Gc4i4rf2B200', '_blank');
    } finally {
      setPortalLoading(false);
    }
  };

  const plan = billing?.plan || {};
  const paymentMethods = billing?.payment_methods || [];
  const hasStripeCustomer = billing?.has_stripe_customer || false;
  const isPaid = plan?.status === 'active' && plan?.amount && plan?.amount !== '£0';

  if (loading) {
    return (
      <div className="profile-section">
        <div className="section-header">
          <div className="section-header-info">
            <h2>Billing & Plan</h2>
            <p>Loading real-time billing and Stripe account details…</p>
          </div>
        </div>
        <div className="billing-loading-spinner-wrap">
          <div className="loading-spinner small"></div>
          <span>Connecting to Stripe…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-section">
      <div className="section-header">
        <div className="section-header-info">
          <h2>Billing & Plan</h2>
          <p>Manage your ivori subscription, active test packages, payment methods, and invoices.</p>
        </div>
        <button
          type="button"
          className="billing-refresh-btn"
          onClick={() => { fetchBilling(); fetchTransactions(); }}
          title="Refresh billing data"
        >
          Refresh
        </button>
      </div>

      {portalError && (
        <div className="billing-card" style={{ borderLeft: '3px solid var(--iv-head)' }}>
          <p className="billing-muted" style={{ color: 'var(--iv-head)', margin: 0 }}>{portalError}</p>
        </div>
      )}

      {/* Current Active Plan */}
      <div className="billing-card">
        <div className="billing-card-row">
          <div className="billing-card-left">
            <div>
              <div className="billing-plan-header-row">
                <h3 className="billing-plan-name">{plan.name || 'Beginnings (Free)'}</h3>
                <span className={`billing-status-tag ${isPaid ? 'paid' : 'free'}`}>
                  {plan.status ? plan.status.toUpperCase() : 'ACTIVE'}
                </span>
              </div>
              <span className="billing-plan-cycle">
                {plan.cycle || 'Free'} {plan.amount && plan.amount !== '£0' ? `· ${plan.amount}` : ''}
              </span>
              <p className="billing-plan-renewal">{plan.renewal_text || 'Free tier — no renewal required.'}</p>
            </div>
          </div>
          <div className="billing-card-actions">
            {hasStripeCustomer && (
              <button
                type="button"
                className="billing-btn billing-btn-outline"
                onClick={handleOpenStripePortal}
                disabled={portalLoading}
              >
                {portalLoading ? 'Opening Stripe Portal…' : 'Manage on Stripe'}
              </button>
            )}
            <button
              type="button"
              className="billing-btn billing-btn-solid"
              onClick={() => navigate('/pricing')}
            >
              {isPaid ? 'View Packages & Upgrades' : 'Upgrade Plan'}
            </button>
          </div>
        </div>
      </div>

      {/* Payment Methods */}
      <div className="billing-card">
        <div className="billing-card-row" style={{ alignItems: 'center', marginBottom: paymentMethods.length > 0 ? '16px' : '0' }}>
          <h4 className="billing-section-title" style={{ margin: 0 }}>Saved Payment Methods</h4>
          {hasStripeCustomer && (
            <button
              type="button"
              className="billing-btn billing-btn-outline"
              onClick={handleOpenStripePortal}
              disabled={portalLoading}
              style={{ fontSize: '12.5px', padding: '6px 14px' }}
            >
              {portalLoading ? 'Opening…' : 'Update Card'}
            </button>
          )}
        </div>
        {paymentMethods.length > 0 ? (
          <div className="billing-payment-methods-list">
            {paymentMethods.map((pm) => {
              const brandKey = (pm.brand || '').toLowerCase();
              const brandIcon = CARD_BRAND_ICONS[brandKey] || CARD_BRAND_ICONS.default;
              return (
                <div key={pm.id} className="billing-card-row billing-pm-row">
                  <div className="billing-card-left" style={{ gap: '12px' }}>
                    <div className="billing-brand-icon">{brandIcon}</div>
                    <div>
                      <span className="billing-card-number">{pm.brand || 'Card'} •••• {pm.last4 || '****'}</span>
                      <span className="billing-card-exp">Expires {pm.exp_month}/{pm.exp_year}</span>
                    </div>
                  </div>
                  {pm.is_default && <span className="billing-default-badge">Default</span>}
                </div>
              );
            })}
          </div>
        ) : (
          <p className="billing-muted">No saved cards on file. Cards will be saved securely upon checkout.</p>
        )}
      </div>

      {/* Transaction History & Invoices */}
      <div className="billing-card">
        <div className="billing-card-row" style={{ alignItems: 'center', marginBottom: '16px' }}>
          <h4 className="billing-section-title" style={{ margin: 0 }}>Invoices & Payment Receipts</h4>
        </div>
        {transactionsLoading ? (
          <p className="billing-muted">Loading transaction records…</p>
        ) : transactions.length > 0 ? (
          <div className="billing-table-wrapper">
            <table className="billing-invoices-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Description</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Receipt</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((txn) => {
                  const statusSlug = (txn.status || '').toLowerCase().replace(/\s+/g, '-');
                  return (
                    <tr key={txn.id}>
                      <td>{txn.date}</td>
                      <td className="billing-txn-desc">{txn.description || 'ivori Package Access'}</td>
                      <td className="billing-txn-total">{txn.total}</td>
                      <td>
                        <span className={`billing-inv-status billing-inv-status--${statusSlug}`}>
                          {txn.status || 'Paid'}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {txn.pdf_url ? (
                          <a
                            href={txn.pdf_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="billing-inv-link"
                          >
                            View Receipt
                          </a>
                        ) : (
                          <span className="billing-muted">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="billing-muted">No transactions found. Invoices and official Stripe PDF receipts will appear here immediately after payment.</p>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;